import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { sportsAPI, profileAPI, usersAPI, api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { PlayerCard } from '@/components/PlayerStatsCard';
import { FifaCard } from '@/components/FifaCard';
import { ImageCropUpload } from '@/components/ImageCropUpload';
import { PLAY_STYLES } from '@/lib/traitMeta';
import { toast } from 'sonner';
import {
  User, Settings, Sun, Moon, LogOut, MapPin, Star, Trophy, Eye, EyeOff, Smile,
  Target, TrendingUp, Award, Calendar, ChevronUp, ChevronDown, Shield,
  Trash2, ExternalLink, Camera
} from 'lucide-react';
import {
  getInitials, getSkillLevelLabel, getRoleLabel, DAYS_OF_WEEK, TIME_SLOTS, resolveMediaUrl
} from '@/lib/utils';

const HeroStat = ({ value, label, sub, accent = 'text-white' }) => (
  <div className="text-center">
    <div className={`text-2xl md:text-3xl font-bold tabular-nums leading-none ${accent}`}>
      {value}
    </div>
    <div className="text-[10px] uppercase tracking-widest text-white/70 mt-1">{label}</div>
    {sub && <div className="text-[11px] text-white/60 mt-0.5">{sub}</div>}
  </div>
);

const FormIndicator = ({ result }) => {
  const styles = {
    W: 'bg-emerald-500 text-white shadow-emerald-500/30',
    L: 'bg-rose-500 text-white shadow-rose-500/30',
    D: 'bg-amber-500 text-white shadow-amber-500/30',
    '?': 'bg-muted text-muted-foreground',
  };
  return (
    <div
      className={`w-7 h-7 rounded-full ${styles[result] || styles['?']} flex items-center justify-center text-xs font-bold shadow`}
      title={result === 'W' ? 'Vittoria' : result === 'L' ? 'Sconfitta' : result === 'D' ? 'Pareggio' : 'N/A'}
    >
      {result}
    </div>
  );
};

const StatTile = ({ icon: Icon, value, label, accent }) => (
  <div className={`rounded-xl p-4 bg-gradient-to-br ${accent}`}>
    <div className="flex items-center justify-between text-white/90">
      <Icon className="h-5 w-5" />
      <span className="text-3xl font-bold tabular-nums leading-none">{value}</span>
    </div>
    <div className="mt-2 text-[11px] uppercase tracking-widest text-white/80">{label}</div>
  </div>
);

const PrivacyTile = ({ item, checked, onToggle }) => (
  <div className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/60 transition-colors">
    <div className="flex items-center gap-3">
      <div className={`h-8 w-8 rounded-full flex items-center justify-center ${checked ? 'bg-emerald-500/15 text-emerald-600' : 'bg-muted text-muted-foreground'}`}>
        {checked ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
      </div>
      <div>
        <Label className="font-medium cursor-pointer">{item.label}</Label>
        <p className="text-xs text-muted-foreground">{item.desc}</p>
      </div>
    </div>
    <Switch checked={checked} onCheckedChange={onToggle} />
  </div>
);

export const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, setUser, playerProfile, refereeProfile, logout, updateProfile, updateRefereeProfile, deleteRefereeProfile } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [sports, setSports] = useState([]);
  const [saving, setSaving] = useState(false);
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);

  const [playerData, setPlayerData] = useState({
    city: playerProfile?.city || '',
    bio: playerProfile?.bio || '',
    phone: playerProfile?.phone || '',
    favorite_sports: playerProfile?.favorite_sports || [],
    skill_level: playerProfile?.skill_level || 3,
    preferred_foot: playerProfile?.sport_profiles?.sport_calcetto?.preferred_foot || null,
    strong_foot: playerProfile?.sport_profiles?.sport_calcetto?.strong_foot ?? 3,
    weak_foot: playerProfile?.sport_profiles?.sport_calcetto?.weak_foot ?? 3,
    play_style: playerProfile?.sport_profiles?.sport_calcetto?.play_style || null,
    availability: playerProfile?.availability || [],
    privacy_settings: playerProfile?.privacy_settings || {
      show_email: false,
      show_phone: false,
      show_city: true,
      show_bio: true,
      show_stats: true,
      show_availability: false,
    }
  });

  // What the calcetto attributes were when this screen loaded (see handleSavePlayer).
  const initialAttrs = useRef({
    skill_level: playerProfile?.skill_level || 3,
    preferred_foot: playerProfile?.sport_profiles?.sport_calcetto?.preferred_foot || null,
    strong_foot: playerProfile?.sport_profiles?.sport_calcetto?.strong_foot ?? 3,
    weak_foot: playerProfile?.sport_profiles?.sport_calcetto?.weak_foot ?? 3,
    play_style: playerProfile?.sport_profiles?.sport_calcetto?.play_style || null,
  });

  // The calcetto card, with the admin's form override winning like everywhere else.
  const calcetto = playerProfile?.sport_profiles?.sport_calcetto || {};
  const ownCard = { ...calcetto, form: calcetto.form_manual ?? calcetto.form };

  const [nickname, setNickname] = useState(user?.nickname || '');
  const [savingNickname, setSavingNickname] = useState(false);
  const [fullName, setFullName] = useState(user?.name || '');
  const [savingName, setSavingName] = useState(false);

  const [deleteConfirmation, setDeleteConfirmation] = useState('');
  const [deletingAccount, setDeletingAccount] = useState(false);

  const [refereeData, setRefereeData] = useState({
    city: refereeProfile?.city || '',
    bio: refereeProfile?.bio || '',
    phone: refereeProfile?.phone || '',
    sports: refereeProfile?.sports || [],
    coverage_radius_km: refereeProfile?.coverage_radius_km || 20,
    is_available: refereeProfile?.is_available ?? true
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [sportsRes, statsRes] = await Promise.all([
          sportsAPI.getAll(),
          api.get('/stats/me').catch(() => ({ data: null })),
        ]);
        setSports(sportsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error loading profile data:', error);
      } finally {
        setStatsLoading(false);
      }
    };
    load();
  }, []);

  const handleSavePlayer = async () => {
    setSaving(true);
    try {
      // ponytail: calcetto block is fixed; read the catalog when a second sport gets self-editable attributes.
      // Send only what changed on this screen: the cached profile may be older
      // than an admin's edit, and resending it would silently overwrite that.
      const { preferred_foot, strong_foot, weak_foot, play_style, skill_level, ...rest } = playerData;
      const changed = {};
      if (skill_level !== initialAttrs.current.skill_level) changed.skill_level = skill_level;
      if (preferred_foot !== initialAttrs.current.preferred_foot) changed.preferred_foot = preferred_foot;
      if (strong_foot !== initialAttrs.current.strong_foot) changed.strong_foot = strong_foot;
      if (weak_foot !== initialAttrs.current.weak_foot) changed.weak_foot = weak_foot;
      if (play_style !== initialAttrs.current.play_style) changed.play_style = play_style;
      await updateProfile({ ...rest, sport_profiles: { sport_calcetto: changed } });
      initialAttrs.current = { skill_level, preferred_foot, strong_foot, weak_foot, play_style };
      toast.success('Profilo aggiornato!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  // ImageCropUpload already uploaded the cropped image; '' means "Rimuovi".
  const handleSavePicture = async (url) => {
    try {
      const res = await profileAPI.updatePicture(url || null);
      setUser(prev => ({ ...prev, picture: res.data.picture || null }));
      if (!url) toast.success('Foto rimossa');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio della foto');
    }
  };

  const handleSaveNickname = async () => {
    setSavingNickname(true);
    try {
      await profileAPI.updateNickname(nickname);
      setUser(prev => ({ ...prev, nickname: nickname || null }));
      toast.success('Soprannome aggiornato!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingNickname(false);
    }
  };

  const handleSaveName = async () => {
    const trimmed = fullName.trim();
    if (trimmed.length < 2) {
      toast.error('Il nome deve avere almeno 2 caratteri');
      return;
    }
    if (trimmed === (user?.name || '')) return;
    setSavingName(true);
    try {
      await profileAPI.updateName(trimmed);
      setUser(prev => ({ ...prev, name: trimmed }));
      setFullName(trimmed);
      toast.success('Nome aggiornato!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSavingName(false);
    }
  };

  const togglePrivacy = (key) => {
    setPlayerData(prev => ({
      ...prev,
      privacy_settings: {
        ...prev.privacy_settings,
        [key]: !prev.privacy_settings[key]
      }
    }));
  };

  const handleSaveReferee = async () => {
    setSaving(true);
    try {
      await updateRefereeProfile(refereeData);
      toast.success('Profilo arbitro aggiornato!');
    } catch (error) {
      toast.error('Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handleLeaveReferees = async () => {
    setSaving(true);
    try {
      await deleteRefereeProfile();
      toast.success('Non compari più tra gli arbitri');
    } catch (error) {
      toast.error('Errore nella rimozione');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmation !== 'ELIMINA') return;
    setDeletingAccount(true);
    try {
      const { data } = await usersAPI.deleteAccount(deleteConfirmation);
      localStorage.removeItem('leaguehub-token');
      if (data.apple_revocation === 'failed') {
        window.alert(
          `Account LeagueHub eliminato. Apple non ha completato la revoca: rimuovi LeagueHub da “Accedi con Apple” seguendo le istruzioni ufficiali:\n${data.apple_revocation_url}`
        );
      }
      window.location.href = '/';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Impossibile eliminare l\'account');
      setDeletingAccount(false);
    }
  };

  const toggleSport = (sportId, isPlayer = true) => {
    if (isPlayer) {
      const current = playerData.favorite_sports;
      const updated = current.includes(sportId)
        ? current.filter(s => s !== sportId)
        : [...current, sportId];
      setPlayerData({ ...playerData, favorite_sports: updated });
    } else {
      const current = refereeData.sports;
      const updated = current.includes(sportId)
        ? current.filter(s => s !== sportId)
        : [...current, sportId];
      setRefereeData({ ...refereeData, sports: updated });
    }
  };

  const toggleAvailability = (day, slot) => {
    const current = playerData.availability;
    const dayEntry = current.find(a => a.day === day);

    let updated;
    if (dayEntry) {
      const slots = dayEntry.slots.includes(slot)
        ? dayEntry.slots.filter(s => s !== slot)
        : [...dayEntry.slots, slot];

      if (slots.length === 0) {
        updated = current.filter(a => a.day !== day);
      } else {
        updated = current.map(a => a.day === day ? { ...a, slots } : a);
      }
    } else {
      updated = [...current, { day, slots: [slot] }];
    }

    setPlayerData({ ...playerData, availability: updated });
  };

  const isSlotSelected = (day, slot) => {
    const dayEntry = playerData.availability.find(a => a.day === day);
    return dayEntry?.slots.includes(slot) || false;
  };

  const winRate = stats?.win_rate ?? 0;
  const recentForm = stats?.recent_form || [];
  const isReferee = user?.role === 'referee' || user?.role === 'admin';

  const privacyItems = [
    { key: 'show_email', label: 'Email', desc: 'Il tuo indirizzo email' },
    { key: 'show_phone', label: 'Telefono', desc: 'Il tuo numero di telefono' },
    { key: 'show_city', label: 'Città', desc: 'La tua città' },
    { key: 'show_bio', label: 'Bio', desc: 'La tua descrizione personale' },
    { key: 'show_stats', label: 'Statistiche', desc: 'Le tue statistiche di gioco globali' },
    { key: 'show_availability', label: 'Disponibilità', desc: 'I tuoi orari di disponibilità' },
  ];

  return (
    <div className="pb-24" data-testid="profile-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-violet-600" />
        <div className="pointer-events-none absolute -top-20 -right-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-20 w-72 h-72 rounded-full bg-white/10 blur-3xl" />

        <div className="relative px-4 md:px-8 pt-8 pb-12 md:pt-10 md:pb-16">
          <div className="max-w-5xl mx-auto">
            <div className="flex flex-col sm:flex-row items-center sm:items-end gap-5">
              <div className="relative shrink-0">
                <Avatar className="h-24 w-24 md:h-28 md:w-28 ring-4 ring-white/40 ring-offset-4 ring-offset-transparent shadow-2xl">
                  <AvatarImage src={resolveMediaUrl(user?.picture)} alt={user?.name} />
                  <AvatarFallback className="text-3xl bg-white/20 text-white">
                    {getInitials(user?.name)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div className="flex-1 min-w-0 text-center sm:text-left">
                <div className="flex items-center gap-2 flex-wrap justify-center sm:justify-start">
                  <h1 className="text-2xl md:text-3xl font-bold text-white drop-shadow">{user?.name}</h1>
                  {user?.nickname && (
                    <span className="text-base md:text-lg italic text-white/90">«{user.nickname}»</span>
                  )}
                </div>
                <p className="text-white/80 text-sm break-all">{user?.email}</p>
                <div className="flex flex-wrap justify-center sm:justify-start gap-2 mt-3">
                  <span className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] uppercase tracking-widest font-semibold">
                    {getRoleLabel(user?.role)}
                  </span>
                  {playerProfile?.city && (
                    <span className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] flex items-center gap-1">
                      <MapPin className="h-3 w-3" />
                      {playerProfile.city}
                    </span>
                  )}
                  {playerProfile?.skill_level && (
                    <span className="px-2.5 py-1 rounded-full bg-white/20 backdrop-blur text-white text-[11px] flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      {getSkillLevelLabel(playerProfile.skill_level)}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-2 sm:justify-end shrink-0">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  className="bg-white/15 hover:bg-white/25 backdrop-blur text-white border-0"
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Esci
                </Button>
                <AlertDialog onOpenChange={(open) => !open && setDeleteConfirmation('')}>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="secondary"
                      size="sm"
                      data-testid="delete-account-btn"
                      className="bg-red-500/30 hover:bg-red-500/40 backdrop-blur text-white border border-white/30"
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Elimina account
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Eliminare definitivamente l&apos;account?</AlertDialogTitle>
                      <AlertDialogDescription asChild>
                        <div className="space-y-3">
                          <p>
                            Account, profili, iscrizioni, contenuti e dati personali verranno eliminati.
                            I risultati storici resteranno soltanto in forma anonima. L&apos;operazione non può essere annullata.
                          </p>
                          <div>
                            <Label htmlFor="delete-account-confirmation">
                              Scrivi <strong>ELIMINA</strong> per confermare
                            </Label>
                            <Input
                              id="delete-account-confirmation"
                              className="mt-2"
                              value={deleteConfirmation}
                              onChange={(event) => setDeleteConfirmation(event.target.value)}
                              autoComplete="off"
                            />
                          </div>
                        </div>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Annulla</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleDeleteAccount}
                        disabled={deleteConfirmation !== 'ELIMINA' || deletingAccount}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                        {deletingAccount ? 'Eliminazione...' : 'Elimina account'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>

            {/* Hero stats strip */}
            {stats && !statsLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-6 mt-8">
                <HeroStat value={stats.total_matches ?? 0} label="Partite" />
                <HeroStat value={`${winRate}%`} label="Win rate" />
                <HeroStat value={stats.goals_scored ?? 0} label="Gol fatti" />
                <HeroStat value={stats.leagues_joined ?? 0} label="Leghe" />
              </div>
            )}
            {statsLoading && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 md:gap-6 mt-8">
                {[1, 2, 3, 4].map(i => (
                  <Skeleton key={i} className="h-12 bg-white/15" />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="relative z-10 max-w-5xl mx-auto px-4 md:px-8 -mt-6 space-y-6">
        <Tabs defaultValue="stats" className="space-y-6">
          <Card className="overflow-hidden">
            <TabsList className="w-full justify-start overflow-x-auto no-scrollbar h-auto p-1.5 bg-card">
              <TabsTrigger value="stats" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
                <Trophy className="h-4 w-4 mr-2" />
                Statistiche
              </TabsTrigger>
              <TabsTrigger value="player">
                <User className="h-4 w-4 mr-2" />
                Giocatore
              </TabsTrigger>
              {isReferee && (
                <TabsTrigger value="referee">
                  <Shield className="h-4 w-4 mr-2" />
                  Arbitro
                </TabsTrigger>
              )}
              <TabsTrigger value="privacy">
                <Eye className="h-4 w-4 mr-2" />
                Privacy
              </TabsTrigger>
              <TabsTrigger value="settings">
                <Settings className="h-4 w-4 mr-2" />
                Impostazioni
              </TabsTrigger>
            </TabsList>
          </Card>

          {/* Stats Tab */}
          <TabsContent value="stats" className="space-y-6">
            <div className="mx-auto w-full max-w-[260px]">
              <FifaCard player={user} attributes={ownCard} />
            </div>
            {statsLoading ? (
              <Skeleton className="h-64" />
            ) : !stats ? (
              <Card>
                <CardContent className="p-8 text-center text-muted-foreground">
                  <Trophy className="h-12 w-12 mx-auto mb-2 opacity-40" />
                  <p>Nessuna statistica disponibile</p>
                  <p className="text-sm">Gioca qualche partita per vedere le tue stats</p>
                </CardContent>
              </Card>
            ) : (
              <>
                <PlayerCard
                  userId={user?.user_id}
                  sportId="sport_calcetto"
                  profile={playerProfile?.sport_profiles?.sport_calcetto || playerProfile}
                  showDetailed={false}
                />
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <StatTile icon={Trophy} value={stats.wins} label="Vittorie" accent="from-emerald-500 to-emerald-600" />
                  <StatTile icon={Award} value={stats.draws} label="Pareggi" accent="from-amber-500 to-amber-600" />
                  <StatTile icon={Target} value={stats.losses} label="Sconfitte" accent="from-rose-500 to-rose-600" />
                  <StatTile icon={TrendingUp} value={`${winRate}%`} label="Win rate" accent="from-blue-500 to-indigo-600" />
                </div>

                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2">
                      <TrendingUp className="h-4 w-4 text-primary" />
                      Win rate progress
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-3 rounded-full bg-muted overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-emerald-500 via-blue-500 to-violet-500 transition-all"
                        style={{ width: `${winRate}%` }}
                      />
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-2 tabular-nums">
                      <span>{stats.wins} vinte</span>
                      <span>{stats.total_matches} totali</span>
                    </div>
                  </CardContent>
                </Card>

                {recentForm.length > 0 && (
                  <Card>
                    <CardHeader className="pb-3">
                      <CardTitle className="text-base">Ultimi risultati</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="flex gap-1.5 flex-wrap">
                        {recentForm.map((f, i) => (
                          <FormIndicator key={i} result={f.result} />
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Card>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                        <ChevronUp className="h-6 w-6 text-emerald-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">{stats.goals_scored ?? 0}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Gol fatti</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-rose-500/10 flex items-center justify-center">
                        <ChevronDown className="h-6 w-6 text-rose-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">{stats.goals_conceded ?? 0}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Gol subiti</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-blue-500/10 flex items-center justify-center">
                        <Calendar className="h-6 w-6 text-blue-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">{stats.total_matches ?? 0}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Partite totali</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="h-12 w-12 rounded-xl bg-violet-500/10 flex items-center justify-center">
                        <Award className="h-6 w-6 text-violet-500" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold tabular-nums">{stats.leagues_joined ?? 0}</p>
                        <p className="text-xs text-muted-foreground uppercase tracking-widest">Leghe attive</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Player Profile */}
          <TabsContent value="player" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Nome</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Il nome completo mostrato sul profilo, nelle partite e nelle classifiche.
                </p>
                <div className="flex gap-3">
                  <Input
                    placeholder="Es: Mario Rossi"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    maxLength={80}
                    className="flex-1"
                    data-testid="profile-name-input"
                  />
                  <Button onClick={handleSaveName} disabled={savingName} size="sm" data-testid="profile-name-save">
                    {savingName ? 'Salvo...' : 'Salva'}
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Camera className="h-5 w-5 text-primary" />
                  Foto profilo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  È la foto della tua card, nei membri della lega e nelle partite.
                </p>
                <ImageCropUpload preset="square" round value={user?.picture || ''} onChange={handleSavePicture} />
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Smile className="h-5 w-5 text-primary" />
                  Soprannome
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Scegli un soprannome che verrà mostrato accanto al tuo nome ovunque nella piattaforma.
                </p>
                <div className="flex gap-3">
                  <Input
                    placeholder='Es: "Il Bomber", "Zidane", "La Muraglia"'
                    value={nickname}
                    onChange={(e) => setNickname(e.target.value)}
                    maxLength={30}
                    className="flex-1"
                  />
                  <Button onClick={handleSaveNickname} disabled={savingNickname} size="sm">
                    {savingNickname ? 'Salvo...' : 'Salva'}
                  </Button>
                </div>
                {nickname && (
                  <p className="text-sm text-muted-foreground">
                    Anteprima: <span className="font-medium text-foreground">{user?.name}</span>{' '}
                    <span className="italic text-primary">«{nickname}»</span>
                  </p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Profilo Giocatore</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="city">Città</Label>
                    <Input
                      id="city"
                      placeholder="Es: Milano"
                      value={playerData.city}
                      onChange={(e) => setPlayerData({ ...playerData, city: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="phone">Telefono</Label>
                    <Input
                      id="phone"
                      placeholder="+39 333 1234567"
                      value={playerData.phone}
                      onChange={(e) => setPlayerData({ ...playerData, phone: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bio">Bio</Label>
                  <Textarea
                    id="bio"
                    placeholder="Presentati..."
                    value={playerData.bio}
                    onChange={(e) => setPlayerData({ ...playerData, bio: e.target.value })}
                    rows={3}
                  />
                </div>

                <div className="space-y-3">
                  <Label>Sport Preferiti</Label>
                  <div className="flex flex-wrap gap-2">
                    {sports.map(sport => (
                      <Badge
                        key={sport.sport_id}
                        variant={playerData.favorite_sports.includes(sport.sport_id) ? 'default' : 'outline'}
                        className="cursor-pointer hover:scale-105 active:scale-95 transition-transform"
                        onClick={() => toggleSport(sport.sport_id)}
                      >
                        {sport.name}
                      </Badge>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <Label>Livello di Gioco</Label>
                    <Badge variant="secondary">
                      {getSkillLevelLabel(Math.floor(playerData.skill_level))} · {Number(playerData.skill_level).toFixed(1)}
                    </Badge>
                  </div>
                  <Slider
                    value={[playerData.skill_level]}
                    onValueChange={([value]) => setPlayerData({ ...playerData, skill_level: value })}
                    min={1}
                    max={5}
                    step={0.5}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Principiante</span>
                    <span>Esperto</span>
                  </div>
                </div>

                <div className="space-y-3" data-testid="calcetto-attributes">
                  <Label>Piede preferito (calcetto)</Label>
                  <div className="flex flex-wrap gap-2">
                    {[['right', 'Destro'], ['left', 'Sinistro'], ['both', 'Ambidestro']].map(([key, label]) => (
                      <Badge
                        key={key}
                        variant={playerData.preferred_foot === key ? 'default' : 'outline'}
                        className="cursor-pointer hover:scale-105 active:scale-95 transition-transform"
                        onClick={() => setPlayerData({ ...playerData, preferred_foot: key })}
                      >
                        {label}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <Label>Piede forte</Label>
                    <Badge variant="secondary">{Number(playerData.strong_foot).toFixed(1)}</Badge>
                  </div>
                  <Slider
                    value={[playerData.strong_foot]}
                    onValueChange={([value]) => setPlayerData({ ...playerData, strong_foot: value })}
                    min={1}
                    max={5}
                    step={0.5}
                  />
                  <div className="flex items-center justify-between">
                    <Label>Piede debole</Label>
                    <Badge variant="secondary">{Number(playerData.weak_foot).toFixed(1)}</Badge>
                  </div>
                  <Slider
                    value={[playerData.weak_foot]}
                    onValueChange={([value]) => setPlayerData({ ...playerData, weak_foot: value })}
                    min={1}
                    max={5}
                    step={0.5}
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>Solo il piede buono</span>
                    <span>Ambidestro di fatto</span>
                  </div>
                  <Label>Attitudine in campo</Label>
                  <div className="flex flex-wrap gap-2" data-testid="play-style-options">
                    {PLAY_STYLES.map(({ key, label }) => (
                      <Badge
                        key={key}
                        variant={playerData.play_style === key ? 'default' : 'outline'}
                        className="cursor-pointer hover:scale-105 active:scale-95 transition-transform"
                        onClick={() => setPlayerData({ ...playerData, play_style: key })}
                      >
                        {label}
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-muted-foreground">Serve a dividere attaccanti e difensori in modo equo quando si fanno le squadre.</p>
                </div>

                <div className="space-y-3">
                  <Label>Disponibilità</Label>
                  <div className="grid gap-4">
                    {DAYS_OF_WEEK.map(day => (
                      <div key={day.value} className="space-y-2">
                        <p className="text-sm font-medium">{day.label}</p>
                        <div className="flex flex-wrap gap-2">
                          {TIME_SLOTS.map(slot => (
                            <Badge
                              key={`${day.value}-${slot.value}`}
                              variant={isSlotSelected(day.value, slot.value) ? 'default' : 'outline'}
                              className="cursor-pointer hover:scale-105 active:scale-95 transition-transform"
                              onClick={() => toggleAvailability(day.value, slot.value)}
                            >
                              {slot.label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <Button onClick={handleSavePlayer} disabled={saving} className="w-full">
                  {saving ? 'Salvataggio...' : 'Salva Profilo'}
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Referee Profile */}
          {isReferee && (
            <TabsContent value="referee" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="h-5 w-5 text-primary" />
                    Profilo Arbitro
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="ref-city">Città</Label>
                      <Input
                        id="ref-city"
                        placeholder="Es: Milano"
                        value={refereeData.city}
                        onChange={(e) => setRefereeData({ ...refereeData, city: e.target.value })}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="ref-phone">Telefono</Label>
                      <Input
                        id="ref-phone"
                        placeholder="+39 333 1234567"
                        value={refereeData.phone}
                        onChange={(e) => setRefereeData({ ...refereeData, phone: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ref-bio">Bio</Label>
                    <Textarea
                      id="ref-bio"
                      placeholder="Descrivi la tua esperienza..."
                      value={refereeData.bio}
                      onChange={(e) => setRefereeData({ ...refereeData, bio: e.target.value })}
                      rows={3}
                    />
                  </div>

                  <div className="space-y-3">
                    <Label>Sport</Label>
                    <div className="flex flex-wrap gap-2">
                      {sports.map(sport => (
                        <Badge
                          key={sport.sport_id}
                          variant={refereeData.sports.includes(sport.sport_id) ? 'default' : 'outline'}
                          className="cursor-pointer hover:scale-105 active:scale-95 transition-transform"
                          onClick={() => toggleSport(sport.sport_id, false)}
                        >
                          {sport.name}
                        </Badge>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Raggio di Copertura</Label>
                      <Badge variant="secondary">{refereeData.coverage_radius_km} km</Badge>
                    </div>
                    <Slider
                      value={[refereeData.coverage_radius_km]}
                      onValueChange={([value]) => setRefereeData({ ...refereeData, coverage_radius_km: value })}
                      min={5}
                      max={100}
                      step={5}
                    />
                  </div>

                  <div className="flex items-center justify-between p-4 rounded-lg bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20">
                    <div>
                      <Label className="font-medium">Disponibile</Label>
                      <p className="text-sm text-muted-foreground">
                        Ricevi richieste di arbitraggio
                      </p>
                    </div>
                    <Switch
                      checked={refereeData.is_available}
                      onCheckedChange={(checked) => setRefereeData({ ...refereeData, is_available: checked })}
                    />
                  </div>

                  {refereeProfile && (
                    <div className="grid grid-cols-3 gap-3">
                      <div className="rounded-xl p-4 bg-gradient-to-br from-amber-500 to-orange-500 text-white">
                        <Star className="h-4 w-4 mb-2" />
                        <p className="text-2xl font-bold tabular-nums">
                          {refereeProfile.average_rating?.toFixed(1) || 'N/A'}
                        </p>
                        <p className="text-[11px] uppercase tracking-widest text-white/80">Rating</p>
                      </div>
                      <div className="rounded-xl p-4 bg-gradient-to-br from-blue-500 to-indigo-500 text-white">
                        <Award className="h-4 w-4 mb-2" />
                        <p className="text-2xl font-bold tabular-nums">{refereeProfile.total_reviews || 0}</p>
                        <p className="text-[11px] uppercase tracking-widest text-white/80">Recensioni</p>
                      </div>
                      <div className="rounded-xl p-4 bg-gradient-to-br from-violet-500 to-purple-600 text-white">
                        <Trophy className="h-4 w-4 mb-2" />
                        <p className="text-2xl font-bold tabular-nums">
                          {refereeProfile.current_leagues?.length || 0}/3
                        </p>
                        <p className="text-[11px] uppercase tracking-widest text-white/80">Leghe</p>
                      </div>
                    </div>
                  )}

                  <Button onClick={handleSaveReferee} disabled={saving} className="w-full">
                    {saving ? 'Salvataggio...' : 'Salva Profilo Arbitro'}
                  </Button>
                  {refereeProfile && (
                    <Button variant="outline" onClick={handleLeaveReferees} disabled={saving} className="w-full text-destructive" data-testid="leave-referees">
                      Rimuovimi dagli arbitri
                    </Button>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* Privacy */}
          <TabsContent value="privacy" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="h-5 w-5 text-primary" />
                  Visibilità Profilo
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                <p className="text-sm text-muted-foreground mb-4">
                  Scegli quali informazioni vuoi rendere visibili agli altri giocatori quando visitano il tuo profilo.
                </p>
                {privacyItems.map(item => (
                  <PrivacyTile
                    key={item.key}
                    item={item}
                    checked={playerData.privacy_settings[item.key]}
                    onToggle={() => togglePrivacy(item.key)}
                  />
                ))}
                <div className="pt-4">
                  <Button onClick={handleSavePlayer} disabled={saving} className="w-full">
                    {saving ? 'Salvataggio...' : 'Salva Impostazioni Privacy'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Settings */}
          <TabsContent value="settings" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Impostazioni</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex items-center justify-between p-4 rounded-xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-xl bg-background flex items-center justify-center">
                      {theme === 'dark' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
                    </div>
                    <div>
                      <Label className="font-medium">Tema</Label>
                      <p className="text-sm text-muted-foreground">
                        {theme === 'dark' ? 'Tema scuro' : 'Tema chiaro'}
                      </p>
                    </div>
                  </div>
                  <Switch
                    checked={theme === 'dark'}
                    onCheckedChange={toggleTheme}
                    data-testid="theme-switch"
                  />
                </div>

                <div className="pt-4 border-t">
                  <Button
                    variant="destructive"
                    className="w-full"
                    onClick={handleLogout}
                  >
                    <LogOut className="h-4 w-4 mr-2" />
                    Esci dall&apos;account
                  </Button>
                </div>

                <div className="pt-4 border-t space-y-3">
                  <div>
                    <Label className="font-medium">Dati e account</Label>
                    <p className="text-sm text-muted-foreground">
                      Consulta come trattiamo i tuoi dati personali.
                    </p>
                  </div>
                  <Button asChild variant="outline" className="w-full">
                    <a href="/privacy" target="_blank" rel="noreferrer">
                      <Shield className="h-4 w-4 mr-2" />
                      Informativa sulla privacy
                      <ExternalLink className="h-3.5 w-3.5 ml-2" />
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProfilePage;
